import React, { useState, useEffect } from "react";
import api from "../lib/api.ts";
import { useToast } from "../context/ToastContext";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";

const LeaveReview = () => {
  const { showSuccess, showError } = useToast();
  const { translate } = useLanguage();
  const { isAuthenticated, user } = useAuth();
  const [formData, setFormData] = useState({
    name: "",
    designation: "",
    testimonial: "",
    rating: 5,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isAuthenticated && user?.name) {
      setFormData((prev) => {
        return {
          ...prev,
          name: user.name,
        };
      });
    }
  }, [isAuthenticated, user?.name]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleRatingChange = (rating) => {
    setFormData((prev) => ({
      ...prev,
      rating,
    }));
    if (errors.rating) {
      setErrors((prev) => ({
        ...prev,
        rating: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name =
        translate("testimonials.validation.nameRequired") || "Name is required";
    }

    if (!formData.designation.trim()) {
      newErrors.designation =
        translate("testimonials.validation.designationRequired") ||
        "Designation is required";
    }

    if (!formData.testimonial.trim()) {
      newErrors.testimonial =
        translate("testimonials.validation.testimonialRequired") ||
        "Testimonial is required";
    } else if (formData.testimonial.trim().length < 10) {
      newErrors.testimonial =
        translate("testimonials.validation.testimonialMinLength") ||
        "Testimonial must be at least 10 characters";
    }

    if (formData.rating < 1 || formData.rating > 5) {
      newErrors.rating =
        translate("testimonials.validation.ratingRequired") ||
        "Please select a rating";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if user is logged in
    if (!isAuthenticated) {
      showError(
        translate("testimonials.messages.loginRequired") ||
        "Please login to submit a review"
      );
      return;
    }

    if (!validateForm()) {
      showError(
        translate("testimonials.validation.fixErrorsBeforeSubmitting") ||
        "Please fix the errors before submitting"
      );
      return;
    }

    try {
      setIsSubmitting(true);

      const reviewData = {
        name: formData.name.trim(),
        designation: formData.designation.trim(),
        testimonial: formData.testimonial.trim(),
        rating: formData.rating,
      };

      const response = await api.post("/reviews", reviewData);

      if (response.status === 200 || response.status === 201) {
        showSuccess(
          translate("testimonials.messages.reviewSubmittedSuccessfully") ||
          "Thank you! Your review has been submitted."
        );
        // Reset form but keep name if user is authenticated
        setFormData({
          name: isAuthenticated && user?.name ? user.name : "",
          designation: "",
          testimonial: "",
          rating: 5,
        });
        setErrors({});
      }
    } catch (error) {
      console.error("Error submitting review:", error);
      const errorMessage =
        error.response?.data?.message ||
        translate("testimonials.messages.failedToSubmitReview") ||
        "Failed to submit review. Please try again.";
      showError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-md">
     <div className='bg-card font-medium rounded-t-[8px] sm:rounded-t-[16px]'>
  {/* Standardized container with fixed height and consistent responsive padding */}
  <div className='flex flex-col lg:flex-row lg:items-center justify-between px-4 sm:px-5 py-3 sm:py-4 rounded-t-[8px] sm:rounded-t-[12px] bg-primary-soft min-h-[120px] lg:min-h-[72px]'>
    
    <div className='flex flex-col justify-center'>
      <h2 className='text-white text-base sm:text-lg font-semibold whitespace-nowrap'>
        {translate("dashboard.nav.review") || "Leave a Review"}
      </h2>
    </div>

    {/* Spacer to maintain layout alignment with other headers */}
    <div className='hidden lg:block'></div>
    
  </div>
</div>


      <div className="">
        <div className='rounded-lg shadow-sm p-6'>
          <p className='text-description mb-6'>
            {translate("testimonials.leaveReviewDescription") ||
              "Share your experience with us. Your feedback helps us improve our services."}
          </p>

          {/* Login Required Message */}
          {!isAuthenticated && (
            <div className='p-4 mb-6 border border-yellow-200 rounded-lg bg-yellow-50'>
              <p className='text-sm text-yellow-800'>
                {translate("testimonials.messages.loginRequired") ||
                  "Please login to submit a review"}
              </p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className='space-y-6 max-w-2xl'>
            {/* Name */}
            <div>
              <label className='block text-sm font-medium text-white mb-1'>
                {translate("testimonials.name") || "Name"}{" "}
                <span className='text-red-500'>*</span>
              </label>
              <input
                type='text'
                name='name'
                value={formData.name}
                onChange={handleInputChange}
                placeholder={
                  translate("testimonials.namePlaceholder") || "Enter your name"
                }
                className={`w-full border rounded-lg p-3 text-description border-default bg-page placeholder:text-sm focus:outline-none focus:ring-2 focus:ring-primary ${errors.name ? "border-red-500" : "border-default"
                  }`}
              />
              {errors.name && (
                <p className='text-red-500 text-xs mt-1'>{errors.name}</p>
              )}
            </div>

            {/* Designation */}
            <div>
              <label className='block text-sm font-medium text-white mb-1'>
                {translate("testimonials.designation") || "Designation"}{" "}
                <span className='text-red-500'>*</span>
              </label>
              <input
                type='text'
                name='designation'
                value={formData.designation}
                onChange={handleInputChange}
                placeholder={
                  translate("testimonials.designationPlaceholder") ||
                  "Enter your designation"
                }
                className={`w-full border rounded-lg p-3 text-description border-default bg-page placeholder:text-sm focus:outline-none focus:ring-2 focus:ring-primary ${errors.designation ? "border-red-500" : "border-default"
                  }`}
              />
              {errors.designation && (
                <p className='text-red-500 text-xs mt-1'>{errors.designation}</p>
              )}
            </div>

            {/* Rating */}
            <div>
              <label className='block text-sm font-medium text-white mb-2'>
                {translate("testimonials.rating") || "Rating"}{" "}
                <span className='text-red-500'>*</span>
              </label>
              <div className='flex items-center gap-2'>
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type='button'
                    onClick={() => handleRatingChange(star)}
                    className={`text-3xl transition-colors ${star <= formData.rating ? "text-primary" : "text-default"
                      } hover:text-primary-dark`}
                    aria-label={`Rate ${star} stars`}
                  >
                    ★
                  </button>
                ))}
                <span className='text-sm text-description ml-2'>
                  ({formData.rating}/5)
                </span>
              </div>
              {errors.rating && (
                <p className='text-red-500 text-xs mt-1'>{errors.rating}</p>
              )}
            </div>

            {/* Testimonial */}
            <div>
              <label className='block text-sm font-medium text-white mb-1'>
                {translate("testimonials.testimonial") || "Testimonial"}{" "}
                <span className='text-red-500'>*</span>
              </label>
              <textarea
                name='testimonial'
                value={formData.testimonial}
                onChange={handleInputChange}
                placeholder={
                  translate("testimonials.testimonialPlaceholder") ||
                  "Write your testimonial here..."
                }
                rows={6}
                className={`w-full border rounded-lg p-3 text-description bg-page  placeholder:text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none ${errors.testimonial ? "border-red-500" : "border-default"
                  }`}
              />
              {errors.testimonial && (
                <p className='text-red-500 text-xs mt-1'>{errors.testimonial}</p>
              )}
            </div>

            {/* Submit Button */}
            <div className='flex gap-3 pt-2'>
              <button
                type='submit'
                disabled={isSubmitting || !isAuthenticated}
                className='px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium'
              >
                {isSubmitting
                  ? translate("testimonials.submitting") || "Submitting..."
                  : translate("testimonials.submit") || "Submit Review"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LeaveReview;
